'use client';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { useTheme } from 'next-themes';
import PaginationDots from 'components/commercetools-ui/molecules/product-tile/components/pagination-dots';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { desktop, mobile, smallMobile } from 'helpers/utils/screensizes';
import { fontStyleMap, fontWeightMap } from './config';
import Tile from './tile';
import { Props as TileProps, ProductCategorySliderProps } from './types';
import { positionMap } from './utils';
import Wrapper from '../../../HOC/wrapper';
import { setFFIDsForPagination } from '../curated-slider/helpers';


const Slider = dynamic(() => import('../../atoms/slider'), { ssr: false });

const createTile = (slide: any): TileProps['tile'] => {
  const backgroundImage = slide?.backgroundImage?.media?.file || undefined;
  const image = {
    src: slide?.imageSrc,
    alt: slide?.imageAlt || undefined,
  };

  let title = '';
  if (slide?.headline && Array.isArray(slide.headline) && slide.headline.length > 0) {
    title = slide.headline[0]['headlineLabel'] || '';
  }

  return {
    title: title || '',
    backgroundImage,
    image,
    target: slide?.link || slide?.ctaReference || slide?.target || undefined,
    headLine: slide?.headline || [],
  } as TileProps['tile'];
};

const ProductCategorySlider: React.FC<ProductCategorySliderProps> = (props: ProductCategorySliderProps) => {
  const { headingPostion, primaryHeading, secondaryHeading, autoRotation } = props;
  const { isTouchDevice } = useTouchDevice();
  const [isDesktopSize] = useMediaQuery(desktop);
  const layoutAnchorRef = React.useRef<HTMLDivElement>(null);
  const [snapIndex, setSnapIndex] = React.useState(0);
  const [currentSlidesPerView, setCurrentSlidesPerView] = React.useState(0);
  
  const { resolvedTheme = 'light' } = useTheme();
  const [themeColor, setThemeColor] = React.useState('');

  // Normalize incoming slides (from tastic/data) into Tile props
  const slides = Array.isArray(props?.slides) ? props.slides : [];
  const tiles: TileProps['tile'][] = slides.map(createTile);
  const hasOverflow = tiles.length > currentSlidesPerView;

  let navPosition: 'left' | 'right' = 'right';
  if (headingPostion === 'right') {
    navPosition = 'left';
  }
  const arrowsEdgeClassName = navPosition === 'left' ? 'left-0 bottom-0' : 'right-0 bottom-0';
  const getHeadingStyles = (heading: any, isDesktopSize: boolean) => {
    if (!heading?.[0]) return {};
    return {
      fontSize: isDesktopSize ? `${heading[0].fontSizeDesktop}px` : `${heading[0].fontSizeMobile}px`,
      color: themeColor === 'dark' ? heading[0]?.textColorDark : heading[0].textColor,
    };
  };

  useEffect(() => {
    setThemeColor(resolvedTheme ?? '');
  }, [resolvedTheme]);
  
  console.info("ProductCategorySlider: ", { ...props, tiles });

  return (
    <div ref={layoutAnchorRef} className="mx-20 sm:mx-24 md:mx-75 lg:mx-76">
      <Wrapper
        clearDefaultStyles
        className="curated-slider product-category-slider-container product-category-grid container relative pt-60 pb-16 mb-30"
        data-ffid="ctr-productcategory-slider"
      >
        <div data-ffid="ctr-productcategory-slider-content">
          <div className="w-full overflow-hidden" data-ffid="ctr-productcategory-heading-section">
            <div
              className={`heading-category-slider flex gap-4 px-4 ${navPosition === 'right' ? 'justify-between' : ''}`}
              data-ffid="ctr-productcategory-heading-container"
            >
              <div className={`flex flex-1 text-wrap ${positionMap[headingPostion] || positionMap.center}`}>
                <div className={`max-w-[920px] ${headingPostion === 'center' ? 'ml-8' : ''}`}>
                  {primaryHeading?.[0]?.primaryHeadingLabel && (
                    <h2
                      className={clsx('mx-52', `heading-title`, fontWeightMap[primaryHeading[0].primaryHeadingTextStyle])}
                      style={getHeadingStyles(primaryHeading, isDesktopSize)}
                      data-ffid={`lbl-productcategory-primary-heading`}
                    >
                      {primaryHeading[0].primaryHeadingLabel}
                    </h2>
                  )}
                  {secondaryHeading?.[0]?.secondaryHeadingLabel &&
                    secondaryHeading[0].secondaryHeadingLabel.length > 0 && (
                      <h3
                        className={clsx(
                          'mx-52',
                          `heading-title ${positionMap[headingPostion] || positionMap.center}`,
                          fontStyleMap[secondaryHeading[0].secondaryHeadingTextStyle],
                        )}
                        style={getHeadingStyles(secondaryHeading, isDesktopSize)}
                        data-ffid={`lbl-productcategory-secondary-heading`}
                      >
                        {secondaryHeading[0].secondaryHeadingLabel}
                      </h3>
                    )}
                </div>
              </div>
            </div>
          </div>
          <Slider
            layoutRef={hasOverflow ? layoutAnchorRef : undefined}
            useLayoutRefMarginBox
            a11y={{
              enabled: true,
              prevSlideMessage: 'Previous Slide Promotional',
              nextSlideMessage: 'Next Slide Promotional',
              containerRoleDescriptionMessage: '',
                slideRole: 'presentation',
              itemRoleDescriptionMessage: '',
            }}
            onInit={(sw) => setFFIDsForPagination(sw, 'productcategory')}
            onBreakpoint={(sw) => setCurrentSlidesPerView(sw.params.slidesPerView as number)}
            onSnapIndexChange={(sw) => setSnapIndex(sw.snapIndex)}
            centerInsufficientSlides={true}
            aria-label={`${primaryHeading?.[0]?.primaryHeadingLabel ?? ''} `}
            ariaLabelOfPreviousButton="Previous Slide Promotional"
            ariaLabelOfNextButton="Next Slide Promotional"
            arrows={!isTouchDevice && hasOverflow}
            allowTouchMove={!isDesktopSize || isTouchDevice}
            uniqueFFIDText={'productcategory'}
            slidesPerView={1.4}
            spaceBetween={16}
            arrowsClassName={clsx('z-10', arrowsEdgeClassName)}
            autoplay={
              autoRotation
                ? {
                    delay: props.scrollTime * 1000,
                    disableOnInteraction: true,
                    pauseOnMouseEnter: true,
                  }
                : false
            }
            breakpoints={{
              [smallMobile]: {
                spaceBetween: 16,
                slidesPerView: 1.8,
                centeredSlides: true,
                centeredSlidesBounds: true,
              },
              [mobile]: {
                spaceBetween: 20,
                slidesPerView: 3.3,
                centeredSlides: false,
                centeredSlidesBounds: false,
              },
              [desktop]: {
                spaceBetween: 24,
                slidesPerView: tiles.length > 4 ? 4.2 : 4,
                centeredSlides: false,
                centeredSlidesBounds: false,
              },
            }}
            navPosition={navPosition}
          >
            {tiles.map((tile, index: number) => (
              <Tile
                key={`${tile?.title}-${index}`}
                keyProp={index}
                tile={tile}
                backgroundColor={{ lightColor: props.cardColor, darkColor: props?.cardColorDark }}
                backgroundImage={props?.backImageSrc}
              />
            ))}
          </Slider>
          <div className="flex justify-center mt-20" data-ffid="ctr-product-category-slider-pagination-dots">
            <PaginationDots
              total={tiles.length}
              active={snapIndex}
              slidesPerView={Math.floor(currentSlidesPerView)}
              ffid="productCategorySlider"
            />
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default ProductCategorySlider;
